import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { GetLoginProviderQuery, useCompleteProviderOnboardingMutation } from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { isDirectIntegrationOrgType } from "@pages/Onboarding/ehrConnectionState";
import { ConnectEHRStep } from "@pages/Onboarding/steps/ConnectEHRStep";
import { CreateFirstClientStep } from "@pages/Onboarding/steps/CreateFirstClientStep";
import { GetToKnowYouStep } from "@pages/Onboarding/steps/GetToKnowYouStep";
import { ImportClientsStep } from "@pages/Onboarding/steps/ImportClientsStep";
import { LogoLoader } from "@pages/Onboarding/steps/LogoLoader";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";

// Provider onboarding flow. The shape depends on the provider's role:
//   • BCBAs (bcba / assessment-bcba) get the full multi-step flow, which itself
//     has two shapes based on the org's EHR capability:
//       – EHR org (scraper or direct integration): loader → profile → connect-ehr → import → app
//       – No-EHR org (no scraper, no direct integration): loader → profile → create-client → app
//     (the no-EHR flow swaps connect/import — which never apply to these orgs —
//     for a manual "Create your first client" step). A "Step X of Y" bar shows.
//   • All other providers (psychologist, admin, etc.) get the profile step ONLY —
//     EHR connect + client import are BCBA workflows that don't apply to them — with
//     no step bar (single screen) and a "Finish" primary action that ends onboarding.
//
// The step is driven by the `?step=` query param so each screen is reviewable by
// URL (mirrors the homepage `?state=` preview); `?flow=ehr` / `?flow=no-ehr` force
// the full BCBA flow (and its EHR/no-EHR shape) and `?flow=profile-only` forces the
// non-BCBA single-step view — all regardless of the reviewer's role or org, so both
// paths stay previewable from any account. The flow is not yet auto-gated on first
// login — that waits on a backend onboarding-complete flag (see BACKEND.md).
type OnboardingStep = "loader" | "profile" | "connect-ehr" | "import" | "create-client";

const VALID_STEPS: OnboardingStep[] = ["loader", "profile", "connect-ehr", "import", "create-client"];

export const OnboardingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { organizationType, orgnizationDetailsQuery, roles, loginProviderDetailsQuery } = useAdminData();
  const { isEHREnabled } = useEHRConnection();
  const completeProviderOnboarding = useCompleteProviderOnboardingMutation({});
  const queryClient = useQueryClient();

  // Name the user typed on the profile step, kept in memory so navigating back to
  // that step restores their edits (each step remounts, so the form can't hold it).
  const [profileDraft, setProfileDraft] = React.useState<{ firstName: string; lastName: string } | null>(null);

  // Onboarding done → mark it complete, then hand off to the app's normal
  // post-login landing. Navigating to "/" lets IndexLayout's role-based redirect
  // pick the right page (BCBA → video-sessions, admin → clients, etc.), so this
  // stays correct as that logic evolves (e.g. when the homepage launches and
  // BCBAs are routed to /home). This is the single exit for both Finish and Skip.
  //
  // completeProviderOnboarding flips ProviderMetadata.onboardingCompleted so the
  // first-login gate (OnboardingRedirectGate) won't route the user back here.
  // CRITICAL: we only navigate into the app once completion is confirmed. The gate
  // redirects anyone whose cached onboardingCompleted is `false`, so navigating on
  // a failed mutation — or before the cache reflects success — would just bounce
  // the user straight back into onboarding (an infinite loop). So on failure we
  // stay put and surface a retry; on success we optimistically flip the cached
  // flag (covering a slow/failed refetch) before leaving.
  const finishOnboarding = async () => {
    try {
      const result = await completeProviderOnboarding.mutateAsync({});
      if (!result?.completeProviderOnboarding?.status) {
        throw new Error(result?.completeProviderOnboarding?.message ?? "Onboarding completion did not succeed");
      }
      // Optimistically mark onboarding complete in the shared getLoginProvider
      // cache so the gate sees it as done immediately — even if the background
      // refetch below is slow or fails. Matched by key prefix to AdminDataProvider's
      // getLoginProvider query (["get-login-provider-details", ...]).
      queryClient.setQueriesData<GetLoginProviderQuery>({ queryKey: ["get-login-provider-details"] }, (old) =>
        old?.getLoginProvider?.providermetadata
          ? {
              ...old,
              getLoginProvider: {
                ...old.getLoginProvider,
                providermetadata: { ...old.getLoginProvider.providermetadata, onboardingCompleted: true },
              },
            }
          : old,
      );
    } catch (err) {
      // Stay on the flow (don't navigate) so the gate can't loop the user back
      // here, and give them a way to retry. The mutation is idempotent, so a retry
      // after a transient failure is safe.
      console.warn("completeProviderOnboarding failed during onboarding; staying on the flow", err);
      notifyError("We couldn't finish setting up your account. Please try again.");
      return;
    }
    // The Portal Home Page derives its state from these provider-scoped queries.
    // Onboarding just connected an EHR / imported or created clients, so drop their
    // cached values in this tab — selectHomeState then recomputes from fresh signals
    // when the user lands on /home. (A separate /home tab can't see this invalidate,
    // since the React Query cache isn't shared across tabs; that case is covered by
    // the home queries opting into refetchOnWindowFocus.)
    queryClient.invalidateQueries({ queryKey: ["home-reports"] });
    queryClient.invalidateQueries({ queryKey: ["home-provider-learners"] });

    // Best-effort background sync with the server; the optimistic update above
    // already lets the gate pass, so a refetch failure here is non-fatal.
    void loginProviderDetailsQuery.refetch().catch(() => undefined);
    navigate("/");
  };

  const raw = searchParams.get("step")?.toLowerCase();
  const step: OnboardingStep = (VALID_STEPS as string[]).includes(raw ?? "") ? (raw as OnboardingStep) : "loader";

  // Preview override: `?dialog=connect-ehr` force-opens the connect-required
  // dialog on the import step regardless of org EHR-compatibility, so the dialog
  // is reviewable by URL like the rest of the flow. (The real trigger needs an
  // EHR-compatible, not-yet-connected org.)
  const previewConnectRequiredDialog = searchParams.get("dialog")?.toLowerCase() === "connect-ehr";

  // Preview overrides (URL-driven): `?flow=ehr` / `?flow=no-ehr` force the full
  // BCBA flow — and its EHR vs no-EHR shape — regardless of the reviewer's role or
  // org type, so the multi-step path stays reviewable from any account.
  const flowParam = searchParams.get("flow")?.toLowerCase();
  const noEhrOverride = flowParam === "no-ehr";
  const previewFullFlow = noEhrOverride || flowParam === "ehr";
  // Forces the non-BCBA (profile-only) experience regardless of role, so the
  // single-step view is reviewable from a BCBA/admin account too.
  const previewProfileOnly = flowParam === "profile-only";

  // Only BCBAs get the EHR-connect + client-import steps — those are BCBA
  // workflows. Every other provider role gets the profile step alone. Preview
  // overrides force either shape so both stay reviewable from any account.
  const isBcbaFlow =
    !previewProfileOnly && (previewFullFlow || roles.includes("bcba") || roles.includes("assessment-bcba"));

  // Orgs with neither an EHR scraper login (isEHREnabled) nor a direct API
  // integration (Passage / Therapz / Hipp Health) never connect or import, so
  // they get the manual create-client flow instead of connect-ehr + import.
  const hasEhrIntegration = isEHREnabled || isDirectIntegrationOrgType(organizationType);
  const isNoEhrFlow = noEhrOverride || (!hasEhrIntegration && flowParam !== "ehr");

  // The EHR-vs-no-EHR branch depends on the org's type, which the org-details
  // query supplies. Until it resolves, the branch can't be trusted (an EHR org
  // would look like a no-EHR org), so gate the profile Continue while it's still
  // loading. We treat the query as "ready" once it has SETTLED — success or error
  // — so a failed query (AdminDataProvider sets retry: false) doesn't trap the
  // user on the profile step. The `?flow=no-ehr` preview override also bypasses it.
  const isFlowReady = orgnizationDetailsQuery.isSuccess || orgnizationDetailsQuery.isError || previewFullFlow;

  const goToStep = (next: OnboardingStep) => {
    const params: Record<string, string> = { step: next };
    // Preserve the preview override so it survives step navigation.
    if (flowParam === "no-ehr" || flowParam === "ehr") params.flow = flowParam;
    setSearchParams(params, { replace: true });
  };

  // No-EHR orgs do profile → create-client (2 steps). EHR orgs do profile →
  // connect-ehr → import (3 steps). Direct-integration orgs still pass through
  // connect-ehr, which renders as already-connected for them (see ConnectEHRStep).
  const totalSteps = isNoEhrFlow ? 2 : 3;

  switch (step) {
    case "profile":
      return (
        <GetToKnowYouStep
          draft={profileDraft}
          // BCBAs branch into the EHR vs no-EHR steps, so wait for org details to
          // settle. Non-BCBAs finish here and never branch, so we only wait for
          // roles to load — so we never prematurely show "Finish" to a BCBA.
          continueDisabled={isBcbaFlow ? !isFlowReady : !roles.length}
          continueLabel={isBcbaFlow ? "Continue" : "Finish"}
          onNext={(values) => {
            setProfileDraft(values);
            if (isBcbaFlow) {
              goToStep(isNoEhrFlow ? "create-client" : "connect-ehr");
            } else {
              // Profile is the only step for non-BCBA providers — finishing it
              // completes onboarding.
              finishOnboarding();
            }
          }}
          // Step bar only for the multi-step BCBA flow; the profile-only flow has none.
          currentStep={isBcbaFlow ? 1 : undefined}
          totalSteps={isBcbaFlow ? totalSteps : undefined}
        />
      );
    case "create-client":
      return (
        <CreateFirstClientStep
          onBack={() => goToStep("profile")}
          onFinish={finishOnboarding}
          onSkip={finishOnboarding}
          currentStep={2}
          totalSteps={totalSteps}
        />
      );
    case "connect-ehr":
      return (
        <ConnectEHRStep
          onNext={() => goToStep("import")}
          onBack={() => goToStep("profile")}
          onSkip={() => goToStep("import")}
          currentStep={2}
          totalSteps={totalSteps}
        />
      );
    case "import":
      return (
        <ImportClientsStep
          onBack={() => goToStep("connect-ehr")}
          onFinish={finishOnboarding}
          onSkip={finishOnboarding}
          forceConnectRequiredDialog={previewConnectRequiredDialog}
          currentStep={totalSteps}
          totalSteps={totalSteps}
        />
      );
    case "loader":
    default:
      return <LogoLoader onDone={() => goToStep("profile")} />;
  }
};
